import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

/**
 * Patient Form Validation Rules and Error Messages
 * All validation logic and error messages are centralized here
 */

export class PatientValidators {
  /**
   * Get error message for a form control
   */
  static getErrorMessage(control: AbstractControl | null, fieldName: string): string {
    if (!control || !control.errors || !control.touched) {
      return '';
    }

    const errors = control.errors;

    if (errors['required']) {
      return `* ${fieldName} is required.`;
    }

    if (errors['email']) {
      return 'Please enter a valid email address';
    }

    if (errors['pattern']) {
      return `Please enter a valid ${fieldName}`;
    }

    if (errors['minlength']) {
      return `${fieldName} must be at least ${errors['minlength'].requiredLength} characters`;
    }

    if (errors['maxlength']) {
      return `${fieldName} must not exceed ${errors['maxlength'].requiredLength} characters`;
    }

    if (errors['min']) {
      return `${fieldName} must be at least ${errors['min'].min}`;
    }

    if (errors['max']) {
      return `${fieldName} must not exceed ${errors['max'].max}`;
    }

    // Custom validators
    if (errors['invalidSSN']) {
      return 'Please enter a valid SSN format (XXX-XX-XXXX)';
    }

    if (errors['invalidPhone']) {
      return `* ${fieldName} must be exactly 10 digits.`;
    }

    if (errors['invalidZipCode']) {
      return 'Please enter a valid zip code';
    }

    if (errors['zipNotFound']) {
      return 'Zip code not found. Please verify the value or enter City and State manually.';
    }

    if (errors['invalidNumeric']) {
      return 'Please enter a valid number';
    }

    if (errors['invalidInteger']) {
      return 'Please enter a valid whole number';
    }

    if (errors['positiveNumber']) {
      return `* ${fieldName} is required.`;
    }

    if (errors['timeDifferenceMismatch']) {
      const actual = errors['timeDifferenceMismatch'].actual;
      const expected = errors['timeDifferenceMismatch'].expected;
      return `Time difference (${actual} minutes) does not match slot duration (${expected} minutes).`;
    }

    if (errors['minDurationNotMet']) {
      const actual = errors['minDurationNotMet'].actual;
      const required = errors['minDurationNotMet'].required;
      return `The appointment duration (${actual} minutes) is less than the provider's minimum slot duration (${required} minutes).`;
    }

    if (errors['minSlotDurationNotMet']) {
      const actual = errors['minSlotDurationNotMet'].actual;
      const required = errors['minSlotDurationNotMet'].required;
      return `Slot duration (${actual} minutes) is less than the provider's minimum slot duration (${required} minutes).`;
    }

    return 'Invalid value';
  }

  /**
   * Check if a control has an error and should display it
   */
  static hasError(control: AbstractControl | null): boolean {
    return !!(control && control.invalid && (control.touched || control.dirty));
  }

  /**
   * SSN Validator - Format: XXX-XX-XXXX
   */
  static ssnValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }
      const ssnPattern = /^\d{3}-\d{2}-\d{4}$/;
      return ssnPattern.test(control.value) ? null : { invalidSSN: true };
    };
  }

  /**
   * Phone Number Validator - Must be exactly 10 digits
   */
  static phoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }
      // Remove all non-numeric characters and check if exactly 10 digits
      const numbers = String(control.value).replace(/\D/g, '');
      if (numbers.length === 10) {
        return null;
      }
      return { invalidPhone: true };
    };
  }

  /**
   * Zip Code Validator - 5 digits or 5+4 format
   */
  static zipCodeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }
      const zipPattern = /^\d{5}(-\d{4})?$/;
      return zipPattern.test(control.value) ? null : { invalidZipCode: true };
    };
  }

  /**
   * Numeric Validator - Only allows numbers (integers or decimals)
   */
  static numericValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }
      const numericPattern = /^-?\d*\.?\d+$/;
      return numericPattern.test(String(control.value)) ? null : { invalidNumeric: true };
    };
  }

  /**
   * Integer Validator - Only allows whole numbers
   */
  static integerValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }
      const integerPattern = /^-?\d+$/;
      return integerPattern.test(String(control.value)) ? null : { invalidInteger: true };
    };
  }

  /**
   * Field name mappings for user-friendly error messages
   */
  static fieldNames: { [key: string]: string } = {
    ssn: 'SSN',
    firstName: 'First Name',
    lastName: 'Last Name',
    middleInitial: 'Middle Initial',
    dateOfBirth: 'Date of Birth',
    sex: 'Sex',
    location: 'Location',
    provider: 'Provider',
    practice: 'Practice',
    email: 'Email',
    address1: 'Address 1',
    address2: 'Address 2',
    city: 'City',
    state: 'State',
    zipCode: 'Zip Code',
    homeTel: 'Home Telephone',
    workTel: 'Work Telephone',
    cell: 'Cell Phone',
    // Add more field names as needed
  };

  /**
   * Get user-friendly field name
   */
  static getFieldName(controlName: string): string {
    return this.fieldNames[controlName] || controlName;
  }
}
